import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { getPlatformOrganization, getPlatformPayments } from '../../services/api';
import { formatDateTime, formatMoney } from '../../utils/formatters';
import { ROLE_NAMES } from '../users/roles';
import { PAYMENT_STATUS_LABELS } from './PaymentsCard';

const STATUS_LABELS = { trial: 'Триал', active: 'Активна', suspended: 'Приостановлена' };

const OrgDetailModal = ({ orgId, planById, onImpersonate, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getPlatformOrganization(orgId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setError('Не удалось загрузить организацию'); });
    // Платежи — отдельным запросом: карточка организации полезна и без них,
    // если лента почему-то не отдалась.
    getPlatformPayments({ orgId })
      .then((res) => { if (!cancelled) setPayments(res.payments || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [orgId]);

  const org = data?.organization;

  return (
    <div className="ui-modal" onClick={onClose} role="presentation">
      <div className="ui-modal-box wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ui-modal-head">
          <h2>{org?.name || 'Организация'}</h2>
          <button className="ui-icon-btn" type="button" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" />
            </svg>
          </button>
        </div>

        {!data && !error && <LoadingSpinner />}
        {error && <div className="ui-inline-error">{error}</div>}

        {org && (
          <>
            <dl className="ui-defs">
              <div><dt>Поддомен</dt><dd>{org.slug}</dd></div>
              <div>
                <dt>Статус</dt>
                <dd className={org.status === 'suspended' ? 'ui-text-danger' : ''}>
                  {STATUS_LABELS[org.status] || org.status}
                </dd>
              </div>
              {org.status === 'suspended' && org.suspended_reason && (
                <div><dt>Причина блокировки</dt><dd className="ui-text-danger">{org.suspended_reason}</dd></div>
              )}
              {org.status === 'suspended' && org.suspended_at && (
                <div><dt>Заблокирована</dt><dd>{formatDateTime(org.suspended_at)}</dd></div>
              )}
              <div><dt>Тариф</dt><dd>{planById[org.plan_id]?.name || 'Без тарифа'}</dd></div>
              <div><dt>iiko</dt><dd>{org.iiko_enabled ? (org.iiko_base_url || 'подключён') : 'не подключён'}</dd></div>
              <div><dt>Создана</dt><dd>{formatDateTime(org.created_at)}</dd></div>
              {org.payment_method_title && (
                <div><dt>Способ оплаты</dt><dd>{org.payment_method_title}</dd></div>
              )}
              {org.next_charge_at && (
                <div><dt>Следующее списание</dt><dd>{formatDateTime(org.next_charge_at)}</dd></div>
              )}
              {org.billing_failures > 0 && (
                <div><dt>Неудачных списаний</dt><dd className="ui-text-danger">{org.billing_failures}</dd></div>
              )}
            </dl>

            {onImpersonate && (
              <div className="platform-detail-actions">
                <button type="button" className="ui-btn-ghost" onClick={() => onImpersonate(org.id)}>
                  Войти как эта организация
                </button>
                <span className="ui-dim">Откроется в новой вкладке в режиме только для чтения</span>
              </div>
            )}

            <div className="platform-detail-section">
              <div className="ui-card-head">
                <h2>Платежи ({payments.length})</h2>
              </div>
              {payments.length === 0 ? (
                <div className="ui-empty">Платежей пока не было</div>
              ) : (
                <div className="platform-org-table">
                  {payments.map((payment) => (
                    <div className="platform-org-row" key={payment.id}>
                      <div className="platform-org-main">
                        <div className="platform-org-name">{formatMoney(payment.amount)}</div>
                        <div className="ui-dim">
                          {payment.description || 'Подписка'} · {formatDateTime(payment.paid_at || payment.created_at)}
                        </div>
                      </div>
                      <span className={`ui-status ${payment.status === 'succeeded' ? 'on' : 'off'}`}>
                        {PAYMENT_STATUS_LABELS[payment.status] || payment.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="platform-detail-section">
              <div className="ui-card-head">
                <h2>Пользователи ({data.users.length})</h2>
              </div>
              {data.users.length === 0 ? (
                <div className="ui-empty">Пользователей пока нет</div>
              ) : (
                <div className="platform-org-table">
                  {data.users.map((u) => (
                    <div className="platform-org-row" key={u.id}>
                      <div className="platform-org-main">
                        <div className="platform-org-name">{u.full_name}</div>
                        <div className="ui-dim">{u.username}</div>
                      </div>
                      <span className="ui-badge">{u.role_name || ROLE_NAMES[u.role] || u.role}</span>
                      <span className={`ui-status ${u.is_active ? 'on' : 'off'}`}>
                        {u.is_active ? 'Активен' : 'Заблокирован'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {data.adoption && (
              <div className="platform-detail-section">
                <div className="ui-card-head">
                  <h2>Использование функций</h2>
                </div>
                <div className="ui-list">
                  <div className="ui-list-row">
                    <div className="ui-list-row-title">Telegram-бот организации</div>
                    <span className={`ui-status ${data.adoption.bot_connected ? 'on' : 'off'}`}>
                      {data.adoption.bot_connected ? 'Подключён' : 'Не подключён'}
                    </span>
                  </div>
                  <div className="ui-list-row">
                    <div className="ui-list-row-title">Сотрудники с Telegram</div>
                    <div className="ui-dim">
                      {data.adoption.staff_linked} привязано · сводку получают {data.adoption.digest_enabled}
                    </div>
                  </div>
                  <div className="ui-list-row">
                    <div className="ui-list-row-title">Гости с Telegram (CRM)</div>
                    <div className="ui-dim">{data.adoption.guests_telegram}</div>
                  </div>
                  <div className="ui-list-row">
                    <div className="ui-list-row-title">Цель месяца</div>
                    <div className="ui-dim">
                      {data.adoption.goal_this_month ? formatMoney(data.adoption.goal_this_month) : 'не задана'}
                    </div>
                  </div>
                  <div className="ui-list-row">
                    <div className="ui-list-row-title">Сотрудники и расписание</div>
                    <div className="ui-dim">
                      {data.adoption.employees} в справочнике · смен на 7 дней вперёд: {data.adoption.shifts_next_7_days}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="platform-detail-section">
              <div className="ui-card-head">
                <h2>Последние действия</h2>
              </div>
              {(data.audit_log || []).length === 0 ? (
                <div className="ui-empty">Записей пока нет</div>
              ) : (
                <div className="ui-list">
                  {data.audit_log.map((e) => (
                    <div className="ui-list-row" key={e.id}>
                      <div className="ui-list-row-title">{e.details}</div>
                      <div className="ui-dim">
                        {e.actor_name || 'Система'}{e.actor_is_platform ? ' (супер-админ)' : ''} · {formatDateTime(e.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrgDetailModal;
